import { computed, nextTick } from 'vue';
import store from '@/store';
import { LocalStorageService } from '@/services/LocalStorageService';
import NotificationSettingsRepository from '@/repositories/NotificationSettingsRepository';
import NotificationSettings from '@/models/NotificationSettings';
import { notification } from 'ant-design-vue';
import i18n from '@/i18n';

export function useGlobalNotification() {
    const notificationSettings = computed(() => NotificationSettingsRepository.getAll()?.settings);
    const isGlobalAlertOn = computed<boolean>(() => notificationSettings.value?.GLOBAL_ALERT === true);
    const isLoginAlertOn = computed<boolean>(() => notificationSettings.value?.LOGIN_ALERT === true);

    const isGlobalNotificationVisible = computed<boolean>(() => store.getters['generic/isGlobalNotificationVisible']);
    const isLoginAlertVisibleInStore = computed<boolean>(() => store.getters['generic/isLoginAlertVisible']);

    const initializeGlobalNotification = function () {
        if (isGlobalAlertOn.value) {
            store.dispatch('generic/updateGlobalNotificationVisibility', true);
            return;
        }

        closeGlobalNotification();
    };

    const initializeLoginAlert = function () {
        const key = getLoginAlertStorageKey();

        if (key === null || !isLoginAlertOn.value) {
            setStoreAlertState(false);
            return;
        }

        if (!LocalStorageService.has(key)) {
            setLoginAlertInLocalStorage(true);
            setStoreAlertState(true);
            return;
        }

        const isLoginAlertVisibleInLocalStorage = LocalStorageService.get(key);

        if (isLoginAlertVisibleInLocalStorage === 'false') {
            setStoreAlertState(false);
            return;
        }

        if (isLoginAlertVisibleInLocalStorage === 'true') {
            setStoreAlertState(true);
        }
    };

    const closeGlobalNotification = function () {
        store.dispatch('generic/updateGlobalNotificationVisibility', false);
    };

    const getLoginAlertStorageKey = function () {
        const userId = store.getters['jwtData/userId'];
        return userId == null ? null : `${LocalStorageService.loginAlertKeyPrefix}${userId}`;
    };

    const setLoginAlertInLocalStorage = function (state: boolean) {
        const key = getLoginAlertStorageKey();
        if (key) {
            LocalStorageService.save(key, String(state));
        }
    };

    const setStoreAlertState = function (state: boolean) {
        store.dispatch('generic/updateLoginAlertState', state);
    };

    async function fetchSettings() {
        try {
            await NotificationSettings.getAll();
        } catch (error) {
            notification.error({
                message: i18n.t('Error fetching settings') as string,
                description: '',
            });
        }
    }

    const initializeGlobalNotificationState = async function () {
        await fetchSettings();
        await nextTick();

        initializeGlobalNotification();
        initializeLoginAlert();
    };

    const closeLoginAlert = function () {
        setLoginAlertInLocalStorage(false);
        setStoreAlertState(false);
    };

    return {
        isGlobalAlertOn,
        isLoginAlertOn,
        isGlobalNotificationVisible,
        isLoginAlertVisibleInStore,
        closeGlobalNotification,
        setStoreAlertState,
        initializeGlobalNotificationState,
        closeLoginAlert,
    };
}
